import { Command } from 'commander';
import type { BrandConfig, AuditRecord } from '../branding/types';
import { resolvePaths, resolveProfile } from '../branding/apply';
import { AuditLogStore } from '../storage/audit-log-store';

/**
 * `<brand> audit dump | tail | since <iso8601>`
 *
 * Reads the local JSONL audit log at `${configDir}/audit.jsonl`. Default
 * output format is jsonl (suitable for SIEM piping); `--format=table`
 * renders a human-readable summary.
 */
export function buildAuditCommand(brand: BrandConfig): Command {
  const audit = new Command('audit').description(
    `Inspect the local ${brand.product.title} audit log`,
  );

  audit
    .command('dump')
    .description('Print all audit records')
    .option('--since <iso8601>', 'Only records on or after this timestamp')
    .option('--until <iso8601>', 'Only records on or before this timestamp')
    .option('--job <jobId>', 'Filter to a single job id')
    .option('--format <format>', 'jsonl | table', 'jsonl')
    .option('-p, --profile <name>', 'Config profile whose audit log to read (default "default")')
    .action((opts: { since?: string; until?: string; job?: string; format?: string; profile?: string }) => {
      const records = filter(loadAll(brand, opts.profile), opts);
      render(records, opts.format ?? 'jsonl');
    });

  audit
    .command('tail')
    .description('Print the most recent records')
    .option('-n <count>', 'Number of records to print', '20')
    .option('--format <format>', 'jsonl | table', 'jsonl')
    .option('-p, --profile <name>', 'Config profile whose audit log to read (default "default")')
    .action((opts: { n?: string; format?: string; profile?: string }) => {
      const all = loadAll(brand, opts.profile);
      const count = Number.parseInt(opts.n ?? '20', 10) || 20;
      const records = all.slice(Math.max(0, all.length - count));
      render(records, opts.format ?? 'jsonl');
    });

  audit
    .command('since <iso8601>')
    .description('Print records on or after a timestamp')
    .option('--format <format>', 'jsonl | table', 'jsonl')
    .option('-p, --profile <name>', 'Config profile whose audit log to read (default "default")')
    .action((iso: string, opts: { format?: string; profile?: string }) => {
      const records = filter(loadAll(brand, opts.profile), { since: iso });
      render(records, opts.format ?? 'jsonl');
    });

  return audit;
}

function loadAll(brand: BrandConfig, profile?: string): AuditRecord[] {
  const paths = resolvePaths(brand, resolveProfile(brand, profile));
  return new AuditLogStore(paths.auditFile).readAll();
}

function filter(
  records: AuditRecord[],
  opts: { since?: string; until?: string; job?: string },
): AuditRecord[] {
  let out = records;
  if (opts.since) {
    const t = new Date(opts.since).getTime();
    out = out.filter((r) => new Date(r.completedAt).getTime() >= t);
  }
  if (opts.until) {
    const t = new Date(opts.until).getTime();
    out = out.filter((r) => new Date(r.completedAt).getTime() <= t);
  }
  if (opts.job) {
    out = out.filter((r) => r.jobId === opts.job);
  }
  return out;
}

function render(records: AuditRecord[], format: string): void {
  if (format === 'table') {
    if (records.length === 0) {
      console.log('No records.');
      return;
    }
    console.log(
      ['completedAt', 'jobId', 'executor', 'status', 'durationMs', 'tokens'].join('\t'),
    );
    for (const r of records) {
      const tokens = (r.inputTokens ?? 0) + (r.outputTokens ?? 0);
      console.log(
        [r.completedAt, r.jobId, r.executor, r.status, r.durationMs, tokens].join('\t'),
      );
    }
    return;
  }
  for (const r of records) {
    console.log(JSON.stringify(r));
  }
}
